import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  alpha,
  useTheme,
} from "@mui/material";
import {
  ChevronRight,
  Warning,
  Info,
  CheckCircle,
  Error,
} from "@mui/icons-material";
import { formatDate } from "../../utils/formatters";
import { memo } from "react";

const RecentItem = ({
  timestamp,
  title,
  subtitle,
  severity,
  type,
  deviceId,
  onItemClick,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const getSeverityColor = (sev) => {
    const colors = {
      critical: theme.palette.error.main,
      high: theme.palette.warning.main,
      medium: theme.palette.info.main,
      low: theme.palette.success.main,
      enter: theme.palette.success.main,
      exit: theme.palette.error.main,
      info: theme.palette.info.main,
    };
    return colors[sev?.toLowerCase()] || theme.palette.text.secondary;
  };

  const getSeverityIcon = (sev) => {
    const iconProps = { fontSize: "small" };
    switch (sev?.toLowerCase()) {
      case "critical":
        return <Error {...iconProps} />;
      case "high":
        return <Warning {...iconProps} />;
      case "medium":
        return <Info {...iconProps} />;
      case "low":
        return <CheckCircle {...iconProps} />;
      case "enter":
        return <CheckCircle {...iconProps} />;
      case "exit":
        return <Error {...iconProps} />;
      default:
        return <Info {...iconProps} />;
    }
  };

  const handleClick = () => {
    if (deviceId && onItemClick) {
      onItemClick(deviceId);
    } else if (deviceId) {
      navigate(`/device/${deviceId}`);
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        mb: 2,
        cursor: deviceId ? "pointer" : "default",
        transition: "all 0.2s ease-in-out",
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderRadius: 2,
        "&:hover": deviceId
          ? {
              backgroundColor: alpha(theme.palette.action.hover, 0.1),
              transform: "translateY(-2px)",
              boxShadow: theme.shadows[4],
            }
          : {},
      }}
      onClick={handleClick}
    >
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box flex={1} mr={2}>
            <Box display="flex" alignItems="center" mb={0.5}>
              <Box
                sx={{
                  backgroundColor: alpha(getSeverityColor(severity), 0.1),
                  color: getSeverityColor(severity),
                  borderRadius: 1,
                  p: 0.5,
                  mr: 1,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {getSeverityIcon(severity)}
              </Box>
              <Typography variant="body2" fontWeight={500} noWrap>
                {title}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {formatDate(timestamp, "LLL")}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                color="text.primary"
                display="block"
                sx={{ mt: 0.5 }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box display="flex" alignItems="center">
            <Box
              sx={{
                backgroundColor: alpha(getSeverityColor(severity), 0.1),
                color: getSeverityColor(severity),
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "uppercase",
                mr: 1,
              }}
            >
              {severity || type}
            </Box>
            {deviceId && (
              <IconButton
                size="small"
                sx={{ color: theme.palette.text.secondary }}
              >
                <ChevronRight fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default memo(RecentItem);
